import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Form,
  Grid,
  Image,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchTaskTypes } from '../../store/slices/taskSlice';
import { monitoringApi } from '../../services/monitoring';
import { RecentProcessedTaskRecord } from '../../types';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const { Text, Link } = Typography;

const imageExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
const videoExtensions = ['.mp4', '.mov', '.webm', '.mkv'];

const isImageUrl = (value: string) => {
  const lowerValue = value.toLowerCase();
  return imageExtensions.some((extension) => lowerValue.includes(extension));
};

const isVideoUrl = (value: string) => {
  const lowerValue = value.toLowerCase();
  return videoExtensions.some((extension) => lowerValue.includes(extension));
};

const formatDuration = (value?: number | null) => {
  if (value === null || value === undefined) {
    return '-';
  }

  if (value < 1000) {
    return `${value} ms`;
  }

  return `${(value / 1000).toFixed(value >= 10_000 ? 1 : 2)} s`;
};

const MediaPreview: React.FC<{
  urls: string[];
  files: string[];
  kind: 'source' | 'result';
}> = ({ urls, files, kind }) => {
  const limitedUrls = urls.slice(0, 3);

  if (!urls.length) {
    return <Text type="secondary">-</Text>;
  }

  return (
    <Space wrap size={[8, 8]}>
      {limitedUrls.map((url, index) => {
        const file = files[index] || url;
        const mediaLabel = kind === 'source' ? `原图${index + 1}` : `结果${index + 1}`;

        if (isVideoUrl(url) || isVideoUrl(file)) {
          return (
            <div key={`${url}-${index}`} style={{ width: 96 }}>
              <video
                src={url}
                controls
                preload="metadata"
                style={{
                  width: 96,
                  height: 96,
                  objectFit: 'cover',
                  borderRadius: 8,
                  background: '#000',
                  display: 'block',
                }}
              />
              <Link href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                {mediaLabel}
              </Link>
            </div>
          );
        }

        if (isImageUrl(url) || isImageUrl(file)) {
          return (
            <div key={`${url}-${index}`} style={{ width: 96 }}>
              <Image
                src={url}
                alt={mediaLabel}
                width={96}
                height={96}
                style={{ objectFit: 'cover', borderRadius: 8 }}
                fallback=""
                placeholder={
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f5f5f5',
                      borderRadius: 8,
                    }}
                  >
                    加载中
                  </div>
                }
              />
              <Link href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                {mediaLabel}
              </Link>
            </div>
          );
        }

        return (
          <div key={`${url}-${index}`} style={{ maxWidth: 120 }}>
            <Tag color="default" style={{ marginBottom: 4 }}>
              文件
            </Tag>
            <div>
              <Link href={url} target="_blank" rel="noopener noreferrer">
                查看文件
              </Link>
            </div>
          </div>
        );
      })}
      {urls.length > limitedUrls.length && (
        <Tag color="blue">+{urls.length - limitedUrls.length} 个文件</Tag>
      )}
    </Space>
  );
};

const RecentProcessedTasks: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { taskTypes } = useSelector((state: RootState) => state.task);
  const { autoRefresh, refreshInterval } = useSelector((state: RootState) => state.settings);
  const screens = Grid.useBreakpoint();

  const [form] = Form.useForm();
  const [records, setRecords] = useState<RecentProcessedTaskRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    size: 20,
    total: 0,
  });
  const paginationRef = useRef(pagination);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const refreshData = useCallback(
    async (page?: number, size?: number) => {
      const nextPage = page ?? paginationRef.current.current;
      const nextSize = size ?? paginationRef.current.size;
      setLoading(true);

      try {
        const values = form.getFieldsValue();
        const response = await monitoringApi.getRecentProcessedTasks({
          envList: values.envList?.length ? values.envList : undefined,
          taskType: values.taskType || undefined,
          page: nextPage,
          size: nextSize,
        });

        const data = response.data;
        const sortedRecords = [...data.records].sort((a, b) => b.createdAt - a.createdAt);
        const nextPagination = {
          current: data.current,
          size: data.size,
          total: data.total,
        };

        setRecords(sortedRecords);
        paginationRef.current = nextPagination;
        setPagination((previous) => (
          previous.current === nextPagination.current &&
          previous.size === nextPagination.size &&
          previous.total === nextPagination.total
            ? previous
            : nextPagination
        ));
      } catch (error) {
        message.error('获取最近处理任务失败');
      } finally {
        setLoading(false);
      }
    },
    [form]
  );

  useAutoRefresh({
    interval: refreshInterval,
    enabled: autoRefresh,
    onRefresh: () => refreshData(),
  });

  useEffect(() => {
    refreshData(1, paginationRef.current.size);
  }, [refreshData]);

  useEffect(() => {
    if (!taskTypes.length) {
      dispatch(fetchTaskTypes());
    }
  }, [dispatch, taskTypes.length]);

  const taskTypeOptions = useMemo(
    () =>
      taskTypes.map((item) => ({
        label: `${item.name} (${item.type})`,
        value: item.type,
      })),
    [taskTypes]
  );

  const handleSearch = () => {
    refreshData(1, pagination.size);
  };

  const handleReset = () => {
    form.resetFields();
    refreshData(1, pagination.size);
  };

  const handleTableChange = (nextPagination: TablePaginationConfig) => {
    refreshData(nextPagination.current || 1, nextPagination.pageSize || pagination.size);
  };

  const columns: ColumnsType<RecentProcessedTaskRecord> = [
    {
      title: '任务 ID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: screens.xs ? 180 : 220,
      ellipsis: true,
      fixed: screens.xs ? 'left' : undefined,
    },
    {
      title: '显示 ID',
      dataIndex: 'displayId',
      key: 'displayId',
      width: 110,
      responsive: ['sm'] as any,
    },
    {
      title: '任务类型',
      key: 'taskType',
      width: screens.xs ? 140 : 200,
      render: (_, record) => (
        <div>
          <div>{record.taskName || '-'}</div>
          <Text type="secondary">{record.taskType}</Text>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      defaultSortOrder: 'descend',
      sorter: (a, b) => b.createdAt - a.createdAt,
      render: (value: number) => new Date(value).toLocaleString(),
    },
    {
      title: '执行时长',
      dataIndex: 'processingTime',
      key: 'processingTime',
      width: 120,
      render: (value: number | null | undefined) => formatDuration(value),
    },
    {
      title: '原始图片',
      key: 'sourceFileUrls',
      width: 360,
      render: (_, record) => (
        <MediaPreview urls={record.sourceFileUrls || []} files={record.sourceFiles || []} kind="source" />
      ),
    },
    {
      title: '结果媒体',
      key: 'resultFileUrls',
      width: 360,
      render: (_, record) => (
        <MediaPreview urls={record.resultFileUrls || []} files={record.resultFiles || []} kind="result" />
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: screens.xs ? '18px' : '24px' }}>最近处理任务</h1>
          <Text type="secondary">
            展示 TaskCenter 最近处理完成的主任务，默认按创建时间倒序排列。媒体签名链接有效期为 10 分钟，失效后可刷新列表。
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => refreshData()} loading={loading}>
          刷新
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout={screens.xs ? 'vertical' : 'inline'}
          onFinish={handleSearch}
        >
          <Form.Item name="envList" label="环境" style={{ marginBottom: 8 }}>
            <Select
              mode="tags"
              allowClear
              placeholder="输入环境，如 test / prod"
              style={{ width: screens.xs ? '100%' : 260 }}
              tokenSeparators={[',', ' ']}
            />
          </Form.Item>
          <Form.Item name="taskType" label="任务类型" style={{ marginBottom: 8 }}>
            <Select
              allowClear
              showSearch
              placeholder="全部任务类型"
              style={{ width: screens.xs ? '100%' : 260 }}
              options={taskTypeOptions}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Space wrap>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table
          rowKey="taskId"
          loading={loading}
          dataSource={records}
          columns={columns}
          scroll={{ x: 1500 }}
          locale={{
            emptyText: (
              <Empty
                description="暂无最近处理完成的任务"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.size,
            total: pagination.total,
            showSizeChanger: !screens.xs,
            showQuickJumper: !screens.xs,
            showTotal: (total) => `共 ${total} 条记录`,
            size: screens.xs ? 'small' : 'default',
          }}
          onChange={handleTableChange}
          size={screens.xs ? 'small' : 'middle'}
        />
      </Card>
    </div>
  );
};

export default RecentProcessedTasks;
